import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import React, { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { nationalityOptions, noop } from '@blockpulse3/data/shared';
import { RepresentativeRole } from '@blockpulse3/graphql/hooks';
import { alpha3CountryOptions } from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { naturalRepresentativeFormSchema, representativeFormDefaults } from '../../schema';
import { IRepresentativeForm } from '../../types';

type Props = {
  /* ** Default form values ** */
  defaultValues?: IRepresentativeForm;
  /* ** Is the Cancel button disabled ** */
  shouldCancel?: boolean;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit?: (data: IRepresentativeForm) => void;
};

/**
 * NaturalRepresentantForm.
 * ExistingCompany.
 * Natural version of the RepresentantForm. Uses a forwardRef function to be able to submit it
 * from the outside.
 */
export const NaturalRepresentativeInformationsForm = React.forwardRef<HTMLFormElement, Props>(
  (props, ref) => {
    const {
      defaultValues = representativeFormDefaults,
      shouldCancel = false,
      onCancel = noop,
      onSubmit = noop,
    } = props;

    const i18nGeneral = useTranslations('General');
    const i18nRoleValues = useTranslations('RoleValues');
    const i18nRepresentantInformationsForm = useTranslations('NaturalRepresentantInformationsForm');

    const { register, control, formState, handleSubmit, reset } = useForm<IRepresentativeForm>({
      defaultValues,
      resolver: yupResolver(naturalRepresentativeFormSchema),
    });

    const handleFormSubmit: SubmitHandler<IRepresentativeForm> = (data): void => {
      onSubmit(data);
    };

    const handleFormCancel = (): void => {
      onCancel();
    };

    useEffect(() => {
      reset({ ...defaultValues });
    }, [reset, defaultValues]);

    return (
      <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="8">
          <Stack spacing="4">
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <Controller
                control={control}
                name="role"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.role}>
                    <FormLabel htmlFor="role">{i18nRoleValues('RoleField')}</FormLabel>
                    <RadioGroup id="role" {...field}>
                      <HStack spacing="4">
                        <Radio value={RepresentativeRole.PRESIDENT}>
                          {i18nRoleValues(RepresentativeRole.PRESIDENT)}
                        </Radio>
                        <Radio value={RepresentativeRole.COMPANY_MANAGER}>
                          {i18nRoleValues(RepresentativeRole.COMPANY_MANAGER)}
                        </Radio>
                      </HStack>
                    </RadioGroup>
                    <ErrorMessage error={formState.errors?.role} />
                  </FormControl>
                )}
              />
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.firstName}>
                <FormLabel htmlFor="firstName">
                  {i18nRepresentantInformationsForm('FirstNameField')}
                </FormLabel>
                <Input id="firstName" type="text" {...register('firstName')} />
                <ErrorMessage error={formState.errors?.firstName} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.lastName}>
                <FormLabel htmlFor="lastName">
                  {i18nRepresentantInformationsForm('LastNameField')}
                </FormLabel>
                <Input id="lastName" type="text" {...register('lastName')} />
                <ErrorMessage error={formState.errors?.lastName} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.email}>
                <FormLabel htmlFor="email">
                  {i18nRepresentantInformationsForm('EmailField')}
                </FormLabel>
                <Input id="email" type="text" {...register('email')} />
                <ErrorMessage error={formState.errors?.email} />
              </FormControl>
              <Controller
                control={control}
                name="nationality"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.nationality}>
                    <FormLabel htmlFor="nationalityData">
                      {i18nRepresentantInformationsForm('NationalityField')}
                    </FormLabel>
                    <Select
                      id="nationalityData"
                      menuPlacement="auto"
                      options={nationalityOptions}
                      {...field}
                    />
                    <ErrorMessage error={formState.errors?.nationality?.value} />
                  </FormControl>
                )}
              />
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.birthdate}>
                <FormLabel htmlFor="birthdate">
                  {i18nRepresentantInformationsForm('BirthdateField')}
                </FormLabel>
                <Input id="birthdate" type="date" {...register('birthdate')} />
                <ErrorMessage error={formState.errors?.birthdate} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.birthplace}>
                <FormLabel htmlFor="birthplace">
                  {i18nRepresentantInformationsForm('BirthplaceField')}
                </FormLabel>
                <Input id="birthplace" type="text" {...register('birthplace')} />
                <ErrorMessage error={formState.errors?.birthplace} />
              </FormControl>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nRepresentantInformationsForm('Address.Title')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.line}>
                    <FormLabel htmlFor="line">
                      {i18nRepresentantInformationsForm('Address.LineField')}
                    </FormLabel>
                    <Input id="line" type="string" {...register('address.line')} />
                    <ErrorMessage error={formState.errors?.address?.line} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.address?.city}>
                    <FormLabel htmlFor="city">
                      {i18nRepresentantInformationsForm('Address.CityField')}
                    </FormLabel>
                    <Input id="city" type="string" {...register('address.city')} />
                    <ErrorMessage error={formState.errors?.address?.city} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                    <FormLabel htmlFor="zipcode">
                      {i18nRepresentantInformationsForm('Address.ZipCodeField')}
                    </FormLabel>
                    <Input id="zipcode" type="string" {...register('address.postalCode')} />
                    <ErrorMessage error={formState.errors?.address?.postalCode} />
                  </FormControl>
                  <Controller
                    control={control}
                    name="address.country"
                    render={({ field }): JSX.Element => (
                      <FormControl isInvalid={!!formState.errors.address?.country}>
                        <FormLabel htmlFor="country">
                          {i18nRepresentantInformationsForm('Address.CountryField')}
                        </FormLabel>
                        <Select
                          id="country"
                          menuPlacement="auto"
                          options={alpha3CountryOptions}
                          {...field}
                        />
                        <ErrorMessage error={formState.errors.address?.country?.value} />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Button
              isDisabled={!shouldCancel}
              type="button"
              variant="secondary"
              w="full"
              onClick={handleFormCancel}
            >
              {i18nGeneral('Delete')}
            </Button>
            <Button type="submit" w="full">
              {i18nGeneral('Submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    );
  },
);

NaturalRepresentativeInformationsForm.displayName = 'NaturalRepresentativeInformationsForm';

export type NaturalRepresentativeInformationsFormProps = Props;
